import { Graphics, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { COLORS, INTRO_FONT, METRICS } from "./intro_theme"

export type ButtonVariant = "primary" | "ghost"

interface LoginButtonProps {
    label: string
    width: number
    variant: ButtonVariant
    onClick: () => void
    height?: number
}

/**
 * A real button for the login card: fixed size, a label centred in it, and hover/press states.
 *
 * The intro used to render bare Text ("Login", "Submit", "Cancel") with a click handler on the
 * surrounding container -- no bounds, no hover, no cursor, and a click target that ran the width of
 * the line. This draws an actual control instead.
 */
export class LoginButton extends Graphics {
    private label: Text
    private variant: ButtonVariant
    private w: number
    private h: number
    private hovered = false
    private pressed = false
    private enabled = true

    constructor({ label, width, variant, onClick, height }: LoginButtonProps) {
        super()

        this.variant = variant
        this.w = width
        this.h = height ?? METRICS.buttonHeight

        this.label = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .role("body").bold(false)
            .color(variant === "primary" ? COLORS.onAccent : COLORS.text)
            .align("center")
            .render(label) as Text
        if (variant === "primary") {
            this.label.style.fontWeight = "600"
        } else {
            this.label.style.fontWeight = "500"
        }
        this.addChild(this.label)

        this.interactive = true
        this.cursor = "pointer"

        this.on("pointerover", () => {
            this.hovered = true
            this.paint()
        })
        this.on("pointerout", () => {
            this.hovered = false
            this.pressed = false
            this.paint()
        })
        this.on("pointerdown", () => {
            this.pressed = true
            this.paint()
        })
        this.on("pointerup", () => {
            this.pressed = false
            this.paint()
        })

        const fire = () => {
            if (this.enabled) {
                onClick()
            }
        }
        this.on("click", fire)
        this.on("tap", fire)

        this.paint()
    }

    setEnabled = (enabled: boolean) => {
        this.enabled = enabled
        this.interactive = enabled
        this.cursor = enabled ? "pointer" : "default"
        this.paint()
    }

    setLabel = (text: string) => {
        this.label.text = text
        this.paint()
    }

    private paint = () => {
        this.clear()

        const dim = !this.enabled
        const fillAlpha = dim ? 0.4 : 1

        if (this.variant === "primary") {
            const fill = this.pressed ? COLORS.accentDim : this.hovered ? COLORS.accentHi : COLORS.accent
            this.beginFill(fill, fillAlpha)
            this.drawRoundedRect(0, 0, this.w, this.h, METRICS.buttonRadius)
            this.endFill()
        } else {
            this.lineStyle({ width: 1, color: COLORS.panelRim, alpha: dim ? 0.3 : this.hovered ? 0.8 : 0.55 })
            this.beginFill(0xffffff, this.pressed ? 0.1 : this.hovered ? 0.08 : 0.04)
            this.drawRoundedRect(0, 0, this.w, this.h, METRICS.buttonRadius)
            this.endFill()
            this.lineStyle(0)
        }

        this.label.alpha = dim ? 0.55 : 1
        this.label.x = Math.round((this.w - this.label.width) / 2)
        this.label.y = Math.round((this.h - this.label.height) / 2)
    }
}
